import type { CopyVehicleScheduleFilter, DriveType } from '@/api/main/types.gen'
import { districtLabel } from './autoScheduleDistricts'
import { driveTypeLabel } from './autoScheduleRequirements'
import { formatIntervalClock, type TimeInterval } from './autoScheduleIntervals'
import type { VehicleScheduleMapping } from './autoScheduleCopy'

export type ConfirmLine = {
  label: string
  value: string
}

export const yesNo = (value: boolean, yes: string, no: string): string => (value ? yes : no)

export const formatDistrictNames = (
  districtIds: number[],
  districts: Array<{ id: number; district_code: string; subdistrict_code: string }>
): string => {
  const names = districtIds.map(id => {
    const district = districts.find(item => item.id === id)
    return district ? districtLabel(district) : String(id)
  })
  return names.join(', ')
}

export const requirementsConfirmLines = ({
  vehicleId,
  interval,
  districtIds,
  districts,
  driveTypeCombinations,
  ignoreCooldown,
  onlyEvidence,
  labels,
}: {
  vehicleId: string
  interval: TimeInterval
  districtIds: number[]
  districts: Array<{ id: number; district_code: string; subdistrict_code: string }>
  driveTypeCombinations: DriveType[][]
  ignoreCooldown: boolean
  onlyEvidence: boolean
  labels: {
    vehicle: string
    interval: string
    districts: string
    driveTypes: string
    ignoreCooldown: string
    evidenceMode: string
    yes: string
    no: string
    evidenceOnly: string
    allDriveTypes: string
  }
}): ConfirmLine[] => [
  { label: labels.vehicle, value: vehicleId.toUpperCase() },
  { label: labels.interval, value: formatIntervalClock(interval) },
  { label: labels.districts, value: formatDistrictNames(districtIds, districts) },
  {
    label: labels.driveTypes,
    value: driveTypeCombinations.map(driveTypeLabel).join(', ') || labels.allDriveTypes,
  },
  { label: labels.ignoreCooldown, value: yesNo(ignoreCooldown, labels.yes, labels.no) },
  { label: labels.evidenceMode, value: onlyEvidence ? labels.evidenceOnly : labels.no },
]

export const districtsConfirmLines = ({
  vehicleId,
  interval,
  districtIds,
  districts,
  driveType,
  labels,
}: {
  vehicleId: string
  interval: TimeInterval
  districtIds: number[]
  districts: Array<{ id: number; district_code: string; subdistrict_code: string }>
  driveType: DriveType[]
  labels: {
    vehicle: string
    interval: string
    districts: string
    driveTypes: string
  }
}): ConfirmLine[] => [
  { label: labels.vehicle, value: vehicleId.toUpperCase() },
  { label: labels.interval, value: formatIntervalClock(interval) },
  { label: labels.districts, value: formatDistrictNames(districtIds, districts) },
  { label: labels.driveTypes, value: driveTypeLabel(driveType) },
]

export const copyConfirmLines = ({
  sourceDate,
  destinationDate,
  filter,
  mappings,
  filterLabels,
  labels,
}: {
  sourceDate: string
  destinationDate: string
  filter: CopyVehicleScheduleFilter
  mappings: VehicleScheduleMapping[]
  filterLabels: Record<CopyVehicleScheduleFilter, string>
  labels: {
    sourceDate: string
    destinationDate: string
    filter: string
    mappings: string
  }
}): ConfirmLine[] => [
  { label: labels.sourceDate, value: sourceDate },
  { label: labels.destinationDate, value: destinationDate },
  { label: labels.filter, value: filterLabels[filter] },
  {
    label: labels.mappings,
    value: mappings
      .map(mapping => `${mapping.sourceVehicleId.toUpperCase()}→${mapping.destinationVehicleId.toUpperCase()}`)
      .join(', '),
  },
]
